import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MenuItem } from '@mui/material';
import { Badge } from 'react-bootstrap';

import Sidebar from '../../components/sidebar/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import '../accounts/view/ViewAccountPage.css';
import { MUI_TABLE } from '../../utils/muiTableConfig';
import { Money, Username } from '../../utils/privacyMask';
import { formatUnits, fmtRelative, INVOICE_STATUS_COLOURS } from '../../utils/wallets';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Every invoice, and the place an underpayment gets resolved.
 *
 * Paid and underpaid invoices are what matter here: the bot announces a full
 * payment by itself, but it deliberately never acts on a short one, so those
 * sit waiting for a human. Hence the default filter.
 */
export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [accountsMap, setAccountsMap] = useState({});
  const [status, setStatus] = useState('open,underpaid');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  async function load(nextStatus = status) {
    setLoading(true);
    try {
      const params = nextStatus ? `?status=${nextStatus}&limit=500` : '?limit=500';
      const [invRes, accRes] = await Promise.all([
        axios.get(`${API}/invoice${params}`, { headers: headers() }),
        axios.get(`${API}/account`, { headers: headers() }).catch(() => ({ data: [] })),
      ]);

      setInvoices(Array.isArray(invRes.data) ? invRes.data : []);
      const map = {};
      (accRes.data || []).forEach((a) => { map[a._id] = a.username; });
      setAccountsMap(map);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  async function act(invoice, action) {
    try {
      await axios.post(`${API}/invoice/${invoice._id}/${action}`, {}, { headers: headers() });
      setNotice(`Invoice ${String(invoice._id).slice(-8)} ${action === 'cancel' ? 'cancelled' : 're-quoted'}.`);
      load(status);
    } catch (err) {
      setError(err.response?.data?.error || `Could not ${action} that invoice`);
    }
  }

  /** What actually landed, against what was owed. */
  function receivedCell(invoice) {
    if (!invoice.paidRaw || invoice.paidRaw === '0') {
      return <span style={{ color: '#64748b' }}>—</span>;
    }
    const paid = formatUnits(invoice.paidRaw, invoice.decimals, invoice.symbol === 'USDC' ? 2 : 8);
    const pct = Math.round((invoice.paidRatio || 0) * 100);
    const short = pct < 98;
    return (
      <span style={{ color: short ? '#fb923c' : '#34d399', fontWeight: 600 }}>
        {paid} {invoice.symbol} <span style={{ opacity: 0.7 }}>({pct}%)</span>
      </span>
    );
  }

  const columns = useMemo(() => [
    {
      header: 'Status', accessorKey: 'status', size: 120,
      Cell: ({ cell }) => (
        <Badge style={{ background: INVOICE_STATUS_COLOURS[cell.getValue()] || '#64748b', fontWeight: 600 }}>
          {cell.getValue()}
        </Badge>
      ),
    },
    {
      header: 'Account', accessorKey: 'accountID', size: 150,
      Cell: ({ cell }) => {
        const id = cell.getValue();
        if (!id) return <span style={{ color: '#64748b' }}>—</span>;
        return (
          <Link to={`/account/view/${id}`} style={{ color: '#818cf8' }}>
            <Username value={accountsMap[id] || String(id).slice(-6)} />
          </Link>
        );
      },
    },
    { header: 'For', accessorKey: 'label', size: 180 },
    { header: 'Kind', accessorKey: 'kind', size: 100 },
    {
      header: 'Owed', accessorKey: 'amountEur', size: 100,
      Cell: ({ cell }) => <Money value={cell.getValue()} />,
    },
    {
      header: 'In crypto', accessorKey: 'amountCrypto', size: 150,
      Cell: ({ row }) => (
        <span style={{ fontSize: '0.82rem' }}>
          {row.original.amountCrypto} {row.original.symbol}
        </span>
      ),
    },
    {
      header: 'Received', accessorKey: 'paidRaw', size: 160, enableSorting: false,
      Cell: ({ row }) => receivedCell(row.original),
    },
    {
      header: 'Wallet', accessorKey: 'walletIndex', size: 90,
      Cell: ({ cell }) => <span style={{ fontWeight: 700 }}>#{cell.getValue()}</span>,
    },
    {
      header: 'Created', accessorKey: 'createdAt', size: 120,
      Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{fmtRelative(cell.getValue())}</span>,
    },
  ], [accountsMap]);

  const table = useMaterialReactTable({
    columns,
    data: invoices,
    enableColumnFilterModes: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableGlobalFilter: true,
    initialState: {
      sorting: [{ id: 'createdAt', desc: true }],
      columnPinning: { right: ['mrt-row-actions'] },
    },
    renderRowActionMenuItems: ({ row }) => {
      const open = row.original.status === 'open' || row.original.status === 'expired';
      return [
        <MenuItem key="requote" disabled={!open} onClick={() => act(row.original, 'requote')} sx={{ m: 0, gap: 1 }}>
          <i className="bi bi-arrow-repeat" style={{ fontSize: '0.9rem', color: '#64748b' }} />Re-quote
        </MenuItem>,
        <MenuItem key="cancel" disabled={!open} onClick={() => act(row.original, 'cancel')} sx={{ m: 0, gap: 1 }}>
          <i className="bi bi-x-circle" style={{ fontSize: '0.9rem', color: '#f87171' }} />Cancel
        </MenuItem>,
      ];
    },
    ...MUI_TABLE,
  });

  const FILTERS = [
    { label: 'Needs attention', value: 'open,underpaid' },
    { label: 'Paid', value: 'paid' },
    { label: 'All', value: '' },
  ];

  if (loading) return <LoadingSpinner />;

  const underpaid = invoices.filter((i) => i.status === 'underpaid');

  return (
    <>
      <Sidebar title="Invoices" />
      <div className="view-page">
        {error && (
          <div style={{
            padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)',
            border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem',
          }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}
        {notice && (
          <div style={{
            padding: '12px 16px', marginBottom: 20, background: 'rgba(52,211,153,0.08)',
            border: '1px solid rgba(52,211,153,0.2)', borderRadius: 10, color: '#34d399', fontSize: '0.875rem',
          }}>
            <i className="bi bi-check-circle-fill me-2" />{notice}
          </div>
        )}

        {underpaid.length > 0 && (
          <div style={{
            padding: '12px 16px', marginBottom: 20, background: 'rgba(251,146,60,0.08)',
            border: '1px solid rgba(251,146,60,0.25)', borderRadius: 10, color: '#fb923c', fontSize: '0.875rem',
          }}>
            <i className="bi bi-exclamation-circle-fill me-2" />
            <strong>{underpaid.length}</strong> invoice{underpaid.length === 1 ? '' : 's'} came in short.
            The bot does not act on a partial payment — these need a human.
          </div>
        )}

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-receipt-cutoff" style={{ color: '#06b6d4' }} />Invoices
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setStatus(f.value)}
                  style={{
                    padding: '5px 11px', borderRadius: 999, fontSize: '0.78rem', cursor: 'pointer',
                    border: `1px solid ${status === f.value ? '#4F46E5' : 'var(--color-border)'}`,
                    background: status === f.value ? 'rgba(79,70,229,0.12)' : 'transparent',
                    color: status === f.value ? '#818cf8' : '#94a3b8',
                  }}
                >
                  {f.label}
                </button>
              ))}
              <span className="view-panel-count">{invoices.length}</span>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>
    </>
  );
}
